import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { canAccessAdmin } from '../../utils/roles';
import { CategoryMenu } from '../../types/content';
import { contentApi } from '../../services/contentApi';
import { AnimatePresence, motion } from 'framer-motion';
import IDCoderBrandLockup from '../brand/IDCoderBrandLockup';
import { PROGRAM_SERIES } from '../../content/programSeries';
import { Course } from '../../types';
import { fetchActiveCourses, fetchAppliedCourseIds, programDetailPath } from '../../utils/courses';
import { getUserProfileId } from '../../utils/userProfile';
import { dashboardPath, dashboardTabFromPath, isDashboardTab } from '../../utils/dashboardTabs';
import { isCourseFree } from '../../utils/currency';
import { isNavMenuVisible, navMenuLabel } from '../../utils/navMenus';
import { hasVerifiedEmail } from '../../utils/platformAccess';
import { usePublicNav } from '../../hooks/usePublicNav';
import { focusPageTop } from '../../utils/focusPageTop';

const PROGRAMS_MENU_LIMIT = 6;

function FreeTag() {
  return (
    <span className="flex-shrink-0 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 ring-1 ring-emerald-200">
      Free
    </span>
  );
}

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isCoursesOpen, setIsCoursesOpen] = useState(false);
  const [isProgramsMenuOpen, setIsProgramsMenuOpen] = useState(false);
  const [isDashboardMenuOpen, setIsDashboardMenuOpen] = useState(false);
  const [courseMenu, setCourseMenu] = useState<CategoryMenu[]>([]);
  const { navItems, hasPublishedEvents, hasPublishedGigs } = usePublicNav();
  const [hoveredCategoryId, setHoveredCategoryId] = useState<number | null>(null);
  const [openMobileCategoryId, setOpenMobileCategoryId] = useState<number | null>(null);
  const [programs, setPrograms] = useState<Course[]>([]);
  const [appliedCourseIds, setAppliedCourseIds] = useState<Set<number>>(new Set());
  const { isAuthenticated, isProfileComplete, user, logout, token } = useAuth();
  const location = useLocation();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const coursesMenuRef = useRef<HTMLDivElement>(null);
  const programsMenuRef = useRef<HTMLDivElement>(null);
  const dashboardMenuRef = useRef<HTMLDivElement>(null);
  const emailVerified = hasVerifiedEmail(user);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsUserMenuOpen(false);
    setIsMenuOpen(false);
    setIsCoursesOpen(false);
    setIsProgramsMenuOpen(false);
    setIsDashboardMenuOpen(false);
    setOpenMobileCategoryId(null);
    setOpenMobilePrograms(false);
  }, [location.pathname]);

  const [openMobilePrograms, setOpenMobilePrograms] = useState(false);

  useEffect(() => {
    if (!isMenuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMenuOpen]);

  useEffect(() => {
    const loadMenu = () => {
      contentApi.courseMenu().then((r) => setCourseMenu(r.data.categories || [])).catch(() => undefined);
    };
    loadMenu();
    window.addEventListener('idcoder-catalog-changed', loadMenu);
    return () => window.removeEventListener('idcoder-catalog-changed', loadMenu);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchActiveCourses()
      .then((list) => { if (!cancelled) setPrograms(list); })
      .catch(() => { if (!cancelled) setPrograms([]); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const profileId = getUserProfileId(user);
    if (!isAuthenticated || !profileId) {
      setAppliedCourseIds(new Set());
      return;
    }
    let cancelled = false;
    fetchAppliedCourseIds(profileId, token)
      .then((ids) => { if (!cancelled) setAppliedCourseIds(ids); })
      .catch(() => { if (!cancelled) setAppliedCourseIds(new Set()); });
    return () => { cancelled = true; };
  }, [isAuthenticated, user?.id, token]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (coursesMenuRef.current && !coursesMenuRef.current.contains(event.target as Node)) {
        setIsCoursesOpen(false);
      }
      if (programsMenuRef.current && !programsMenuRef.current.contains(event.target as Node)) {
        setIsProgramsMenuOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (dashboardMenuRef.current && !dashboardMenuRef.current.contains(event.target as Node)) {
        setIsDashboardMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const dashboardHref = (() => {
    if (location.pathname.startsWith('/dashboard')) {
      const tab = new URLSearchParams(location.search).get('tab');
      return dashboardPath(isDashboardTab(tab) ? tab : 'courses');
    }
    return dashboardPath(dashboardTabFromPath(location.pathname));
  })();
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);
  const activeCategory = courseMenu.find((c) => c.id === hoveredCategoryId) || courseMenu[0];
  const closeOtherMenus = () => {
    setIsCoursesOpen(false);
    setIsProgramsMenuOpen(false);
    setIsDashboardMenuOpen(false);
  };
  const openCoursesMenu = () => {
    setIsProgramsMenuOpen(false);
    setIsDashboardMenuOpen(false);
    setIsCoursesOpen(true);
  };
  const openProgramsMenu = () => {
    setIsCoursesOpen(false);
    setIsDashboardMenuOpen(false);
    setIsProgramsMenuOpen(true);
  };

  const navChip = (active: boolean) => `nav-chip ${active ? 'nav-chip--active' : ''}`;

  const NavLink = ({ to, label }: { to: string; label: string }) => (
    <Link
      to={to}
      onClick={() => focusPageTop()}
      className={navChip(isActive(to))}
    >
      {label}
    </Link>
  );
  const navFlags = { hasPublishedEvents, hasPublishedGigs };
  const menuVisible = (key: string) => isNavMenuVisible(key, navItems, navFlags);
  const menuLabel = (key: string, fallback: string) => navMenuLabel(key, navItems, fallback);
  const programsNavVisible = menuVisible('programs');
  const interviewsNavVisible = menuVisible('interviews');
  const contactNavVisible = menuVisible('contact');
  const gigsNavVisible = menuVisible('gigs');

  return (
    <header className={`sticky top-0 z-[80] site-header ${isScrolled ? 'site-header--scrolled' : ''}`}>
      <div className="max-w-7xl mx-auto">
        <div className="site-header__bar flex justify-between items-center px-3 sm:px-4 py-2 sm:py-2.5 gap-3">
          <Link to="/" className="shrink-0 min-w-0" onClick={() => focusPageTop()}>
            <span className="sm:hidden"><IDCoderBrandLockup size="sm" /></span>
            <span className="hidden sm:inline"><IDCoderBrandLockup /></span>
          </Link>

          <nav className="hidden lg:flex items-center gap-x-1 xl:gap-x-1.5">
            <NavLink to="/" label="Home" />

            {menuVisible('courses') && (
              <div className="relative" ref={coursesMenuRef} onMouseEnter={openCoursesMenu} onMouseLeave={() => setIsCoursesOpen(false)}>
                <button
                  type="button"
                  className={navChip(isActive('/courses'))}
                  onClick={() => (isCoursesOpen ? setIsCoursesOpen(false) : openCoursesMenu())}
                >
                  {menuLabel('courses', 'Courses')}
                  <ChevronDown className="ml-1 h-4 w-4" />
                </button>
                {isCoursesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute left-0 mt-0 pt-3 w-[48rem] max-w-[min(48rem,calc(100vw-2rem))] z-50"
                  >
                    <div className="flex flex-col rounded-2xl bg-white/95 shadow-2xl ring-1 ring-black/5 overflow-hidden backdrop-blur-xl">
                      <p className="shrink-0 px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Self-paced courses</p>
                      {courseMenu.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-gray-500">No course categories yet.</p>
                      ) : (
                        <div className="grid min-h-0 grid-cols-[18rem_minmax(0,1fr)] h-[min(22rem,60vh)]">
                          <ul className="min-h-0 overflow-y-scroll border-r border-gray-100 py-2 bg-gray-50/80 overscroll-contain [scrollbar-gutter:stable]">
                            {courseMenu.map((category) => {
                              const selected = activeCategory?.id === category.id;
                              return (
                                <li key={category.id}>
                                  <Link
                                    to={`/courses?category=${category.id}`}
                                    className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between gap-2 ${
                                      selected ? 'bg-white text-primary-700 font-semibold' : 'text-gray-700 hover:bg-white hover:text-primary-700'
                                    }`}
                                    onMouseEnter={() => setHoveredCategoryId(category.id)}
                                    onFocus={() => setHoveredCategoryId(category.id)}
                                  >
                                    <span className="min-w-0 break-words">{category.name}</span>
                                    <ChevronDown className="h-3.5 w-3.5 shrink-0 -rotate-90 text-gray-400" />
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                          <div className="min-h-0 overflow-y-auto py-2 overscroll-contain">
                            {activeCategory?.courses.length ? (
                              activeCategory.courses.map((course) => (
                                <div key={course.id} className="px-2 py-1">
                                  <Link
                                    to={`/courses/${course.slug}`}
                                    className="flex items-start justify-between gap-2 px-2 py-1.5 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 rounded-md"
                                  >
                                    <span className="min-w-0">{course.title}</span>
                                    {isCourseFree(course.price, course.discountPercent, course.payable, course.isFree) ? <FreeTag /> : null}
                                  </Link>
                                </div>
                              ))
                            ) : (
                              <p className="px-4 py-3 text-sm text-gray-400">No published courses in this category.</p>
                            )}
                          </div>
                        </div>
                      )}
                      <Link to="/courses" className="shrink-0 block border-t border-gray-100 px-4 py-2.5 text-sm font-medium text-primary-700 hover:bg-primary-50">
                        View all courses
                      </Link>
                    </div>
                  </motion.div>
                )}
              </div>
            )}

            {menuVisible('labs') && <NavLink to="/labs" label={menuLabel('labs', 'Labs')} />}
            {menuVisible('projects') && <NavLink to="/projects" label={menuLabel('projects', 'Projects')} />}
            {menuVisible('events') && <NavLink to="/events" label={menuLabel('events', 'Events')} />}

            {programsNavVisible && (
              <div className="relative" ref={programsMenuRef} onMouseEnter={openProgramsMenu} onMouseLeave={() => setIsProgramsMenuOpen(false)}>
                <button
                  type="button"
                  className={navChip(isActive('/programs'))}
                  onClick={() => (isProgramsMenuOpen ? setIsProgramsMenuOpen(false) : openProgramsMenu())}
                >
                  {menuLabel('programs', PROGRAM_SERIES.navPrograms)}
                  <ChevronDown className="ml-1 h-4 w-4" />
                </button>
                {isProgramsMenuOpen && (
                  <div className="absolute left-0 mt-0 pt-3 w-96 max-w-[min(24rem,calc(100vw-2rem))] z-50">
                    <div className="rounded-2xl bg-white/95 py-2 shadow-2xl ring-1 ring-black/5 backdrop-blur-xl">
                      {programs.length === 0 ? (
                        <p className="px-4 py-2 text-sm text-gray-500">No programs available.</p>
                      ) : (
                        programs.slice(0, PROGRAMS_MENU_LIMIT).map((course) => (
                          <Link
                            key={course.id}
                            to={programDetailPath(course.id)}
                            className="flex items-start justify-between gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700"
                          >
                            <span className="min-w-0 whitespace-normal break-words leading-snug">
                              {course.display_Title || course.title}
                            </span>
                            {appliedCourseIds.has(course.id) && (
                              <span className="flex-shrink-0 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
                                Applied
                              </span>
                            )}
                          </Link>
                        ))
                      )}
                      <Link
                        to={PROGRAM_SERIES.programsListPath}
                        className="mt-1 block border-t border-gray-100 px-4 py-2 text-sm font-medium text-primary-700 hover:bg-primary-50"
                      >
                        View all programs
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            )}

            {interviewsNavVisible && <NavLink to="/interviews" label={menuLabel('interviews', PROGRAM_SERIES.navInterview)} />}
            {contactNavVisible && <NavLink to="/contact" label={menuLabel('contact', 'Contact')} />}
            {gigsNavVisible && <NavLink to="/gigs" label={menuLabel('gigs', 'Gigs')} />}

            {isAuthenticated && canAccessAdmin(user) ? (
              <div className="relative" ref={dashboardMenuRef}>
                <button
                  type="button"
                  className={navChip(isActive('/dashboard') || isActive('/admin/dashboard'))}
                  onClick={() => {
                    closeOtherMenus();
                    setIsDashboardMenuOpen(!isDashboardMenuOpen);
                  }}
                >
                  Dashboard
                  <ChevronDown className="ml-1 h-4 w-4" />
                </button>
                <AnimatePresence>
                  {isDashboardMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="absolute left-0 mt-2 w-48 rounded-2xl bg-white/95 shadow-2xl ring-1 ring-black/5 overflow-hidden z-50 backdrop-blur-xl"
                    >
                      {isProfileComplete ? (
                        <Link to={dashboardHref} className="block px-4 py-2 text-sm hover:bg-primary-50">User dashboard</Link>
                      ) : (
                        <span className="block px-4 py-2 text-sm text-gray-400 cursor-not-allowed">User dashboard</span>
                      )}
                      <Link to="/admin/dashboard" className="block px-4 py-2 text-sm hover:bg-primary-50">Admin dashboard</Link>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              isAuthenticated && isProfileComplete && (
                <Link
                  to={dashboardHref}
                  className={navChip(isActive('/dashboard'))}
                >
                  Dashboard
                </Link>
              )
            )}
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button type="button" className="flex items-center rounded-full py-0.5 pl-0.5 pr-2 hover:bg-white/80 transition-colors" onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-sky-500 flex items-center justify-center mr-1.5 ring-2 ring-white shadow-sm">
                    <span className="text-sm font-semibold text-white">{user?.username?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase()}</span>
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-500" />
                </button>
                <AnimatePresence>
                  {isUserMenuOpen && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="absolute right-0 mt-2 w-48 rounded-2xl bg-white/95 shadow-2xl ring-1 ring-black/5 overflow-hidden backdrop-blur-xl">
                      {isAuthenticated && !emailVerified ? (
                        <Link to="/verify-email" className="block px-4 py-2 text-sm hover:bg-primary-50">Verify email</Link>
                      ) : null}
                      <button type="button" className="w-full text-left px-4 py-2 text-sm hover:bg-primary-50" onClick={logout}>Logout</button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link to="/login" className="nav-cta">Login / Profile</Link>
            )}
          </nav>

          <button
            type="button"
            className="lg:hidden p-2 rounded-full text-slate-700 hover:bg-white/80"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {isMenuOpen && createPortal(
        <div
          className="lg:hidden site-mobile-menu fixed inset-0 z-[200] flex flex-col bg-[color:var(--canvas-base,#eef2ff)]"
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
        >
          <div className="site-mobile-menu__bar flex items-center justify-between px-4 py-3 border-b border-white/40 shrink-0 pt-[max(0.75rem,env(safe-area-inset-top))] bg-white/50 backdrop-blur-xl">
            <IDCoderBrandLockup size="sm" />
            <button type="button" className="p-2 rounded-md text-gray-700 hover:bg-gray-100" aria-label="Close menu" onClick={() => setIsMenuOpen(false)}>
              <X />
            </button>
          </div>
          <nav
            className="flex-1 overflow-y-auto overscroll-contain px-4 py-3 pb-[max(1.5rem,env(safe-area-inset-bottom))]"
            onClick={(event) => {
              if ((event.target as HTMLElement).closest('a')) setIsMenuOpen(false);
            }}
          >
            <Link to="/" className="block py-3 text-base font-medium text-gray-900 border-b border-gray-100">Home</Link>
            {menuVisible('courses') && (
              <div className="border-b border-gray-100">
                <button
                  type="button"
                  className="w-full flex items-center justify-between py-3 text-base font-medium text-gray-900"
                  onClick={() => setOpenMobileCategoryId(openMobileCategoryId == null ? (courseMenu[0]?.id ?? -1) : null)}
                >
                  {menuLabel('courses', 'Courses')}
                  <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${openMobileCategoryId != null ? 'rotate-180' : ''}`} />
                </button>
                {openMobileCategoryId != null && (
                  <div className="pb-3 space-y-1 max-h-[min(18rem,50vh)] overflow-y-auto overscroll-contain">
                    {courseMenu.map((category) => (
                      <div key={category.id}>
                        <button
                          type="button"
                          className="w-full text-left py-2 pl-2 text-sm font-medium text-gray-800"
                          onClick={() => setOpenMobileCategoryId(openMobileCategoryId === category.id ? -1 : category.id)}
                        >
                          {category.name}
                        </button>
                        {openMobileCategoryId === category.id && (
                          <div className="pl-4 pb-1">
                            {category.courses.length === 0 && <p className="text-sm text-gray-400">No published courses yet</p>}
                            {category.courses.map((course) => (
                              <div key={course.id} className="py-1">
                                <Link
                                  to={`/courses/${course.slug}`}
                                  className="flex items-center justify-between gap-2 py-1.5 text-sm text-gray-700"
                                  onClick={() => setIsMenuOpen(false)}
                                >
                                  <span className="min-w-0 break-words">{course.title}</span>
                                  {isCourseFree(course.price, course.discountPercent, course.payable, course.isFree) ? <FreeTag /> : null}
                                </Link>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    <Link to="/courses" className="block py-2 pl-2 text-sm font-semibold text-primary-700">View all courses</Link>
                  </div>
                )}
              </div>
            )}
            {menuVisible('labs') && <Link to="/labs" onClick={() => { setIsMenuOpen(false); focusPageTop(); }} className="block py-3 text-base font-medium text-gray-900 border-b border-gray-100">{menuLabel('labs', 'Labs')}</Link>}
            {menuVisible('projects') && <Link to="/projects" onClick={() => { setIsMenuOpen(false); focusPageTop(); }} className="block py-3 text-base font-medium text-gray-900 border-b border-gray-100">{menuLabel('projects', 'Projects')}</Link>}
            {menuVisible('events') && <Link to="/events" onClick={() => { setIsMenuOpen(false); focusPageTop(); }} className="block py-3 text-base font-medium text-gray-900 border-b border-gray-100">{menuLabel('events', 'Events')}</Link>}
            {programsNavVisible && (
              <div className="border-b border-gray-100">
                <button
                  type="button"
                  className="w-full flex items-center justify-between py-3 text-base font-medium text-gray-900"
                  onClick={() => setOpenMobilePrograms((open) => !open)}
                >
                  {menuLabel('programs', PROGRAM_SERIES.navPrograms)}
                  <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${openMobilePrograms ? 'rotate-180' : ''}`} />
                </button>
                {openMobilePrograms && (
                  <div className="pb-3 space-y-1">
                    {programs.slice(0, PROGRAMS_MENU_LIMIT).map((course) => (
                      <Link key={course.id} to={programDetailPath(course.id)} className="block py-1.5 pl-2 text-sm text-gray-700 break-words">
                        {course.display_Title || course.title}
                      </Link>
                    ))}
                    <Link to="/programs" className="block py-2 pl-2 text-sm font-semibold text-primary-700">View all programs</Link>
                  </div>
                )}
              </div>
            )}
            {interviewsNavVisible && <Link to="/interviews" onClick={() => { setIsMenuOpen(false); focusPageTop(); }} className="block py-3 text-base font-medium text-gray-900 border-b border-gray-100">{menuLabel('interviews', PROGRAM_SERIES.navInterview)}</Link>}
            {contactNavVisible && <Link to="/contact" onClick={() => { setIsMenuOpen(false); focusPageTop(); }} className="block py-3 text-base font-medium text-gray-900 border-b border-gray-100">{menuLabel('contact', 'Contact')}</Link>}
            {gigsNavVisible && <Link to="/gigs" onClick={() => { setIsMenuOpen(false); focusPageTop(); }} className="block py-3 text-base font-medium text-gray-900 border-b border-gray-100">{menuLabel('gigs', 'Gigs')}</Link>}
            {isAuthenticated && isProfileComplete && <Link to={dashboardHref} onClick={() => { setIsMenuOpen(false); focusPageTop(); }} className="block py-3 text-base font-medium text-gray-900 border-b border-gray-100">Dashboard</Link>}
            {isAuthenticated && canAccessAdmin(user) && <Link to="/admin/dashboard" onClick={() => { setIsMenuOpen(false); focusPageTop(); }} className="block py-3 text-base font-medium text-gray-900 border-b border-gray-100">Admin</Link>}
            {isAuthenticated ? (
              <>
                {!emailVerified ? <Link to="/verify-email" className="block py-3 text-base font-medium text-primary-700 border-b border-gray-100">Verify email</Link> : null}
                <button type="button" className="block w-full text-left py-3 text-base font-medium text-gray-900" onClick={() => { setIsMenuOpen(false); logout(); }}>Logout</button>
              </>
            ) : (
              <Link to="/login" className="block py-3 text-base font-medium text-primary-700">Login / Profile</Link>
            )}
          </nav>
        </div>,
        document.body
      )}
    </header>
  );
};

export default Header;
